import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import '../../styles/pages/tara/tara.scss';
import {
  Chat,
  ChatMessage,
  CurrentChatUpdate,
} from '../../interfaces/taraInterfaces';
import generateRandomString from '../../lib/helper-functions/generateRandomString';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import {
  clearStreamingStatusMessages,
  setAllChatsHistory,
  setCurrentChat,
  setHasFetchedTaraChatHistory,
  setHistoryLoading,
  setIsBotTyping,
  setPreviousResponseId,
} from '../../store/reducers/chatSlice';
import {
  setIsSidebarMinimized,
  setIsChatAnimTriggered,
} from '../../store/reducers/uiSlice';
import { allRoutes } from '../../lib/constants/route';
import {
  Navigate,
  Route,
  Routes,
  Location,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import TaraSearch from './search/TaraSearch';
import getCurrentRoute from '../../lib/helper-functions/getCurrentRoute';
import tara_chat_stream_api_call from '../../lib/api/tara/tara-chat-stream-api-call';
import tara_chat_history_api_call from '../../lib/api/tara/tara_chat_history_api_call';
import TaraNewChat from './chat/TaraNewChat';
import CurrentChat from './chat/CurrentChat';
import taraChatAnimationEngine from '../../lib/helper-functions/taraChatAnimationEngine';
import {
  APP_ENV,
  INTERCOM_ENABLED_ENVS,
} from '../../lib/constants/central-api';
import Popup from '../../ui/popup/Popup';
import TaraErrorPopup from './taraUI/TaraErrorPopup';
import { useSearchParams } from 'react-router-dom';
interface LocationState {
  fromHistory?: boolean;
}

interface TaraErrorDetails {
  errorCode?: string | null;
  responseMessage?: string | null;
  chatId?: string | number | null | undefined;
  responseId?: string | null;
  traceId?: string | null;
  userMessage?: string;
  onRetry?: () => void;
  onNewChat?: () => void;
}

const Tara = () => {
  const { t } = useTranslation();
  const location = useLocation() as Location & { state: LocationState };
  const currentRoute = getCurrentRoute(location);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const onboardingUserPrompt = useSelector(
    (state: RootState) => state.user.ai_onboarding.userPrompt
  );

  const allChatsHistory = useSelector(
    (state: RootState) => state.chat.allChatsHistory
  );

  const hasFetchedTaraChatHistory = useSelector(
    (state: RootState) => state.chat.hasFetchedTaraChatHistory
  );

  const currentChat = useSelector((state: RootState) => state.chat.currentChat);
  const isTaraChatAnimationEnabled = useSelector(
    (state: RootState) => state.UI.isTaraChatAnimationEnabled
  );
  const isChatAnimTriggered = useSelector(
    (state: RootState) => state.UI.isChatAnimTriggered
  );

  const currentChatRef = useRef(currentChat);
  const allChatsHistoryRef = useRef(allChatsHistory);

  useEffect(() => {
    currentChatRef.current = currentChat;
  }, [currentChat]);

  useEffect(() => {
    allChatsHistoryRef.current = allChatsHistory;
  }, [allChatsHistory]);

  const [loading, setLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('null');

  const abortControllerRef = useRef<AbortController | null>(null);
  const activeStreamChatIdRef = useRef<string | null>(null);
  const [activeStreamChatId, setActiveStreamChatId] = useState<string | null>(
    null
  );

  const toggleErrorPopupModal = () => {
    setIsErrorPopupOpen(!isErrorPopupOpen);
  };

  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false);
  const [errorPopupData, setErrorPopupData] = useState<TaraErrorDetails | null>(
    null
  );

  /* ------------------------------------------------------------------ */
  // These are for onboarding prompt generation
  const [searchParams, setSearchParams] = useSearchParams();
  const afterDone = searchParams.get('after-onboarding-completed') === 'true';
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!afterDone) return;
    if (!onboardingUserPrompt || onboardingUserPrompt.trim() === '') return;

    firedRef.current = true;

    onCurrentChatUpdate({
      newChatId: generateRandomString(10),
      newChat: onboardingUserPrompt,
      role: 'user',
      messageType: 'text',
    });

    // (optional) strip the param so it won't retrigger on refresh/back
    const next = new URLSearchParams(searchParams);
    next.delete('after-onboarding-completed');
    setSearchParams(next, { replace: true });
  }, [onboardingUserPrompt, afterDone]);
  /* ------------------------------------------------------------------ */

  const onCurrentChatUpdate = ({
    newChatId,
    newChat,
    role,
    debugInfo = null,
    messageType,
  }: CurrentChatUpdate) => {
    const messageObj: ChatMessage = {
      id: newChatId || generateRandomString(10),
      message: newChat,
      messageType,
      debugInfo: debugInfo ? JSON.stringify(debugInfo, null, 2) : null,
      role,
    };

    if (isTaraChatAnimationEnabled) {
      dispatch(setIsSidebarMinimized(true));
      const wasTriggered = isChatAnimTriggered;
      if (!wasTriggered) {
        dispatch(setIsChatAnimTriggered(true));
      }
      setTimeout(() => {
        const currentPath = window.location.pathname;
        if (
          currentPath.startsWith('/tara') &&
          !currentPath.startsWith('/tara/search')
        ) {
          taraChatAnimationEngine('compact', true);
        }
      }, 100);
    }

    if (!currentChat) {
      const tempChatId = `temp_${generateRandomString(10)}`;
      const firstChat: Chat = {
        chatId: tempChatId,
        chatStartedDate: new Date().toISOString(),
        chatLastUpdatedDate: new Date().toISOString(),
        chatMessages: [messageObj],
      };
      dispatch(setCurrentChat(firstChat));
      dispatch(
        setAllChatsHistory([
          ...allChatsHistory,
          {
            chatId: firstChat.chatId,
            chatStartedDate: firstChat.chatStartedDate,
            chatLastUpdatedDate: firstChat.chatLastUpdatedDate,
            chatTitle: messageObj.message || 'Loading...', // Ensure title is set immediately
            chatDescription: '',
          },
        ])
      );
    } else {
      const updatedMessages = [...currentChat.chatMessages, messageObj];
      const updatedChat: Chat = {
        ...currentChat,
        chatLastUpdatedDate: new Date().toISOString(),
        chatMessages: updatedMessages,
      };
      dispatch(setCurrentChat(updatedChat));
    }
  };

  useEffect(() => {
    if (currentRoute === 'tara' && !hasFetchedTaraChatHistory) {
      dispatch(setHistoryLoading('pending'));
      tara_chat_history_api_call(1, 5)
        .then((res) => {
          const historyList = res?.data;
          if (Array.isArray(historyList) && historyList.length > 0) {
            const chatsMetadata = historyList.map((item: any) => ({
              chatId: item.conversation_id,
              chatStartedDate: item.chatStartedDate,
              chatLastUpdatedDate: item.chatLastUpdatedDate,
              chatTitle: item.conversation_summary?.first_chat || '',
              chatDescription: `Total messages: ${
                item.conversation_summary?.total_chat_history ?? 0
              }`,
            }));
            dispatch(setAllChatsHistory(chatsMetadata));
            dispatch(setHasFetchedTaraChatHistory(true));
          } else {
            dispatch(setAllChatsHistory([]));
          }
          dispatch(setHistoryLoading('success'));
          setLoading('success');
        })
        .catch((error) => {
          console.error('Error fetching tara chat history:', error);
          dispatch(setHistoryLoading('error'));
          setLoading('error');
        });
    }
    dispatch(clearStreamingStatusMessages());
  }, [currentRoute]);

  const taraAPICall = async (currentMessages: string) => {
    const invokedWithChatId =
      currentChatRef.current?.chatId?.toString() || null;

    try {
      // Create a new AbortController to allow cancelling the fetch request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Track which chat this stream is for
      activeStreamChatIdRef.current = invokedWithChatId;
      setActiveStreamChatId(invokedWithChatId);

      // Set loading state to 'pending' and clear any streaming status messages
      setLoading('pending');
      dispatch(clearStreamingStatusMessages());

      //* Trigger intercom event when the first Chat with TARA is done (clicked the send button)
      if (INTERCOM_ENABLED_ENVS.includes(APP_ENV)) {
        console.log('INTERCOM Event Triggered: first-chat');
        // @ts-ignore:next-line
        window.Intercom && window.Intercom('trackEvent', 'first-chat');
      }

      // Make the API call to stream response from TARA
      const res = await tara_chat_stream_api_call(
        currentMessages,
        controller.signal
      );

      //❌ Case 1: If API returns error or response is not successful
      // if (!res || res.success !== true) {
      //   const fallbackMsg =
      //     typeof res?.message === 'string'
      //       ? res.message
      //       : 'Sorry, something went wrong. Please try again.';

      //   dispatch(
      //     setMessage({
      //       message: fallbackMsg,
      //       messageType: 'error',
      //     })
      //   );

      //   dispatch(setIsBotTyping(false));
      //   setLoading('null');
      //   dispatch(clearStreamingStatusMessages());

      //   onCurrentChatUpdate({
      //     newChatId: '',
      //     newChat: fallbackMsg,
      //     role: 'ai',
      //     messageType: 'text',
      //   });
      //   return;
      // }
      if (!res || res.success !== true) {
        setErrorPopupData({
          errorCode: res?.error ?? '500',
          responseMessage: res?.message,
          chatId: currentChatRef.current?.chatId,
          responseId: null,
          traceId: null,
          userMessage: 'Something went wrong while trying to reach TARA.',
          onRetry: () => {
            setIsErrorPopupOpen(false);
            if (
              currentChatRef.current &&
              currentChatRef.current?.chatMessages?.length > 0
            ) {
              const last = currentChatRef.current?.chatMessages?.at(-1);
              if (last?.role === 'user') {
                dispatch(setIsBotTyping(true));
                taraAPICall(last?.message);
              }
            }
          },
          onNewChat: () => {
            dispatch(setCurrentChat(null));
            dispatch(setIsBotTyping(false));
            dispatch(setPreviousResponseId(null));
            dispatch(clearStreamingStatusMessages());
            setIsErrorPopupOpen(false);
            navigate('/tara');
          },
        });
        setIsErrorPopupOpen(true);
        setLoading('null');
        dispatch(setIsBotTyping(false));
        dispatch(clearStreamingStatusMessages());
        if (activeStreamChatIdRef.current === invokedWithChatId) {
          activeStreamChatIdRef.current = null;
          setActiveStreamChatId(null);
        }
        return;
      }

      //❌ Case 2: If user manually aborts the API call
      if (res?.aborted) {
        dispatch(setIsBotTyping(false));
        setLoading('null');
        dispatch(clearStreamingStatusMessages());
        if (activeStreamChatIdRef.current === invokedWithChatId) {
          activeStreamChatIdRef.current = null;
          setActiveStreamChatId(null);
        }
        return;
      }

      // ✅ Extract relevant data from successful response
      const newChatId = res.conversation_id;
      const messageData = res.message;
      const debug_data = res.debug_data;
      const response_id = res.response_id;

      // ✅ Case 1: If message is an array of structured chart/text/table blocks
      if (newChatId && Array.isArray(messageData) && messageData.length > 0) {
        const messages: ChatMessage[] = messageData.map((item, index) => {
          let contentToBeUsed: any = '';

          if (item?.type === 'text') {
            contentToBeUsed = item?.content;
          } else if (item?.type === 'chart') {
            if (item?.chart && typeof item.chart === 'object') {
              contentToBeUsed = item.chart;
            } else if (item?.content && typeof item.content === 'object') {
              /* This is a new check since we are getting data from fallback chat history in a different structure */
              contentToBeUsed = item.content;
            }
          } else if (item?.type === 'table') {
            if (item?.table && typeof item.table === 'object') {
              contentToBeUsed = item.table;
            }
          }

          let messageType: 'text' | 'chart' | 'table' = 'text';
          if (item?.type === 'chart') {
            messageType = 'chart';
          } else if (item?.type === 'table') {
            messageType = 'table';
          }

          return {
            id: item.id || generateRandomString(10),
            message: contentToBeUsed,
            messageType,
            debugInfo:
              index === messageData.length - 1 && debug_data
                ? JSON.stringify(debug_data, null, 2)
                : null,
            role: 'ai',
            responseId: index === messageData.length - 1 ? response_id : null,
          };
        });

        handleNewOrTempChat(newChatId, messages, invokedWithChatId);
      }

      // ✅ Case 2: If message is a plain string (text-only response)
      else if (newChatId && typeof messageData === 'string') {
        const messages: ChatMessage[] = [
          {
            id: generateRandomString(10),
            message: messageData,
            messageType: 'text',
            debugInfo: debug_data ? JSON.stringify(debug_data, null, 2) : null,
            role: 'ai',
            responseId: response_id,
          },
        ];
        handleNewOrTempChat(newChatId, messages, invokedWithChatId);
      }

      // ✅ Case 3: If message is a raw object (non-standard structured response)
      else if (
        newChatId &&
        typeof messageData === 'object' &&
        messageData !== null
      ) {
        const messages: ChatMessage[] = [
          {
            id: generateRandomString(10),
            message: JSON.stringify(messageData, null, 2),
            messageType: 'text',
            debugInfo: debug_data ? JSON.stringify(debug_data, null, 2) : null,
            role: 'ai',
            responseId: response_id,
          },
        ];
        handleNewOrTempChat(newChatId, messages, invokedWithChatId);
      }
    } catch (error: any) {
      setErrorPopupData({
        errorCode: error?.name || '500',
        responseMessage:
          error?.message ||
          'Something went wrong while fetching TARA response.',
        chatId: currentChatRef.current?.chatId ?? null,
        responseId: null,
        traceId: null,
        userMessage: 'TARA could not respond due to an unexpected error.',
        onRetry: () => {
          setIsErrorPopupOpen(false);
          if (
            currentChatRef.current &&
            currentChatRef.current.chatMessages?.length > 0
          ) {
            const last = currentChatRef.current?.chatMessages.at(-1);
            if (last?.role === 'user') {
              dispatch(setIsBotTyping(true));
              taraAPICall(last?.message);
            }
          }
        },
        onNewChat: () => {
          dispatch(setCurrentChat(null));
          dispatch(setIsBotTyping(false));
          dispatch(setPreviousResponseId(null));
          dispatch(clearStreamingStatusMessages());
          setIsErrorPopupOpen(false);
          navigate('/tara');
        },
      });
      setIsErrorPopupOpen(true);
      setLoading('null');
      dispatch(setIsBotTyping(false));
      dispatch(clearStreamingStatusMessages());
      if (activeStreamChatIdRef.current === invokedWithChatId) {
        activeStreamChatIdRef.current = null;
        setActiveStreamChatId(null);
      }
    }
    //  catch (error: any) {
    //   //❌ Case 4: Unexpected error thrown (e.g., JSON parsing)
    //   dispatch(
    //     setMessage({
    //       message: 'Error fetching response from TARA. Please try again.',
    //       messageType: 'error',
    //     })
    //   );

    //   dispatch(setIsBotTyping(false));
    //   setLoading('error');

    //   onCurrentChatUpdate({
    //     newChatId: '',
    //     newChat: 'Sorry, something went wrong. Please try again.',
    //     role: 'ai',
    //     messageType: 'text',
    //   });
    // }
  };

  //  Helper function to handle both temp and new chats (applies to string and array messages)
  const handleNewOrTempChat = (
    newChatId: string,
    messages: ChatMessage[],
    invokedWithChatId: string | null
  ) => {
    // ALWAYS use the latest state from refs in async callbacks
    const latestCurrentChat = currentChatRef.current;
    const latestAllChatsHistory = allChatsHistoryRef.current;
    const latestPathname = window.location.pathname;

    const currentUrlChatId = latestPathname.includes('/tara/chat/')
      ? latestPathname.split('/tara/chat/')[1]?.split('/')[0]
      : null;

    const isCurrentChat =
      invokedWithChatId === latestCurrentChat?.chatId?.toString() ||
      invokedWithChatId === currentUrlChatId;

    if (!isCurrentChat) {
      const existingHistoryEntry = latestAllChatsHistory.find(
        (chat) => chat.chatId === newChatId || chat.chatId === invokedWithChatId
      );

      const chatStartedDate =
        existingHistoryEntry?.chatStartedDate || new Date().toISOString();

      const newMeta = {
        chatId: newChatId,
        chatStartedDate,
        chatLastUpdatedDate: new Date().toISOString(),
        chatTitle:
          existingHistoryEntry?.chatTitle ||
          messages.find((msg) => msg.role === 'user')?.message ||
          '',
        chatDescription: `Total messages: ${
          messages.length + (existingHistoryEntry ? 1 : 0)
        }`,
      };

      let updatedHistory = [...latestAllChatsHistory];

      if (invokedWithChatId?.startsWith('temp_')) {
        updatedHistory = updatedHistory.filter(
          (chat) => chat.chatId !== invokedWithChatId
        );
      }

      updatedHistory = updatedHistory.filter(
        (chat) => chat.chatId !== newChatId
      );
      updatedHistory.unshift(newMeta);
      dispatch(setAllChatsHistory(updatedHistory.slice(0, 5)));

      if (activeStreamChatIdRef.current === invokedWithChatId) {
        dispatch(setIsBotTyping(false));
        setLoading('success');
        activeStreamChatIdRef.current = null;
        setActiveStreamChatId(null);
      }
      return;
    }

    // If latestCurrentChat is null or it's a temporary chat, create a new full chat
    if (
      !latestCurrentChat ||
      (typeof latestCurrentChat.chatId === 'string' &&
        latestCurrentChat.chatId.startsWith('temp_'))
    ) {
      const chatStartedDate = new Date().toISOString();

      const newChat: Chat = {
        chatId: newChatId,
        chatStartedDate,
        chatLastUpdatedDate: new Date().toISOString(),
        chatMessages: latestCurrentChat?.chatMessages
          ? [...latestCurrentChat.chatMessages, ...messages]
          : messages,
      };

      dispatch(setCurrentChat(newChat));

      // Replace temp ID in the URL path with real chatId
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        const segments = url.pathname.split('/');
        // Replace the last segment (which is temp_...) with the new chatId
        segments[segments.length - 1] = newChatId;
        url.pathname = segments.join('/');
        window.history.replaceState(null, '', url.toString());
      }

      // Update history: either replace temp or insert new at top
      let updatedHistory = [...latestAllChatsHistory];

      const newMeta = {
        chatId: newChatId,
        chatStartedDate,
        chatLastUpdatedDate: newChat.chatLastUpdatedDate,
        chatTitle:
          newChat.chatMessages.find((msg) => msg.role === 'user')?.message ||
          '',
        chatDescription: `Total messages: ${newChat.chatMessages.length}`,
      };

      // Remove existing temp entry if exists
      updatedHistory = updatedHistory.filter(
        (chat) => chat.chatId !== latestCurrentChat?.chatId
      );

      // Always insert latest chat on top
      updatedHistory.unshift(newMeta);
      updatedHistory = updatedHistory.slice(0, 5); // Trim to top 5 chats

      dispatch(setAllChatsHistory(updatedHistory));
    }

    // If it's a normal existing chat, just append messages
    else {
      const updatedChat: Chat = {
        ...latestCurrentChat,
        chatLastUpdatedDate: new Date().toISOString(),
        chatMessages: [...latestCurrentChat.chatMessages, ...messages],
      };

      dispatch(setCurrentChat(updatedChat));

      const newMeta = {
        chatId: updatedChat.chatId,
        chatStartedDate: updatedChat.chatStartedDate,
        chatLastUpdatedDate: updatedChat.chatLastUpdatedDate,
        chatTitle:
          updatedChat.chatMessages.find((msg) => msg.role === 'user')
            ?.message || '',
        chatDescription: `Total messages: ${updatedChat.chatMessages.length}`,
      };

      // Remove the old item
      const updatedHistory = latestAllChatsHistory.filter(
        (chat) => chat.chatId !== latestCurrentChat.chatId
      );

      // Add the updated chat to top
      updatedHistory.unshift(newMeta);

      dispatch(setAllChatsHistory(updatedHistory.slice(0, 5))); // Trim to top 5
    }

    dispatch(setIsBotTyping(false));
    setLoading('success');
    if (isCurrentChat || activeStreamChatIdRef.current === invokedWithChatId) {
      activeStreamChatIdRef.current = null;
      setActiveStreamChatId(null);
    }
  };

  const cancelAPICall = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      dispatch(setIsBotTyping(false));
      setLoading('null');
      activeStreamChatIdRef.current = null;
      setActiveStreamChatId(null);
      onCurrentChatUpdate({
        newChatId: '',
        newChat: 'Response stopped by user!',
        role: 'ai',
        messageType: 'text',
      });
    }
  };

  useEffect(() => {
    if (currentChat && currentChat.chatMessages.length > 0) {
      const lastMessage =
        currentChat.chatMessages[currentChat.chatMessages.length - 1];
      if (lastMessage.role === 'user') {
        dispatch(setIsBotTyping(true));
        taraAPICall(lastMessage.message);
      }
    }
  }, [currentChat]);

  const backBtnLogic = () => {
    if (location.pathname.includes('/tara/search')) {
      // When on search page, back should go to either:
      // - The chat if we came from a chat (has fromHistory state)
      // - Or the main tara page if not
      if (location.state?.fromHistory && currentChat) {
        return `/tara/chat/${currentChat.chatId}`;
      } else {
        return '/tara';
      }
    } else if (location.pathname.includes('/tara/chat')) {
      // When on chat page, back should go to:
      // - Search page if we came from there (has fromHistory state)
      // - Or undefined (let browser handle) if not
      if (location.state?.fromHistory) {
        return '/tara/search';
      } else {
        return undefined; // Let browser handle normal back navigation
      }
    }
    return undefined; // Default case
  };

  const title =
    currentRoute === 'mcp'
      ? t('MCP-Title')
      : currentRoute === 'settings'
      ? t('TaraAISettings')
      : t('AskTara');

  const subTitle =
    currentRoute === 'mcp'
      ? t('MCP-SubTitle')
      : currentRoute === 'settings'
      ? t('ManageTaraSettings')
      : t('TaraAgent');

  const derivedLoading =
    loading === 'pending' && activeStreamChatId
      ? activeStreamChatId === currentChat?.chatId?.toString()
        ? 'pending'
        : 'null'
      : loading;

  return (
    <>
      {isErrorPopupOpen && errorPopupData && (
        <Popup
          styles={{ backgroundColor: '#fff' }}
          setTrigger={toggleErrorPopupModal} // ✅ Correct type: () => void
          trigger={isErrorPopupOpen}
        >
          <TaraErrorPopup {...errorPopupData} />
        </Popup>
      )}

      <PageWrapperMain>
        <SubPageContentHolder>
          <SubPageInnerContentHolder
            title={title}
            subTitle={subTitle}
            backgroundColor={
              location.pathname === '/tara' ||
              location.pathname.startsWith('/tara/chat/')
                ? 'var(--sidebar-background-color)'
                : 'var(--main-background-color)'
            }
            padding='2rem 0rem'
            backBtnLink={backBtnLogic()}
            backBtnAction={() => {
              dispatch(setCurrentChat(null));
              dispatch(setIsBotTyping(false));
            }}
            isTaraMCP={
              currentRoute === 'tara' ||
              location.pathname.includes('/tara/chat') ||
              currentRoute === 'search'
            }
          >
            <Routes>
              <Route
                path='/'
                element={
                  !currentChat ? (
                    <TaraNewChat
                      onCurrentChatUpdate={onCurrentChatUpdate}
                      cancelAPICall={cancelAPICall}
                      loading={derivedLoading}
                    />
                  ) : (
                    <Navigate to={`/tara/chat/${currentChat.chatId}`} />
                  )
                }
              />
              <Route
                path='/chat/:conversationId'
                element={
                  <CurrentChat
                    onCurrentChatUpdate={onCurrentChatUpdate}
                    cancelAPICall={cancelAPICall}
                    loading={derivedLoading}
                  />
                }
              />
              {allRoutes['/tara/search'] && (
                <Route path='search/*' element={<TaraSearch />} />
              )}
            </Routes>
          </SubPageInnerContentHolder>
        </SubPageContentHolder>
      </PageWrapperMain>
    </>
  );
};

export default Tara;
